import { useState } from "react";
import { T } from "../theme";

// DataSheet.jsx — shared "data sheet" chrome for the Map and Validate steps
// (design_handoff_wizard_v2 step 1). One component owns:
//   · row paging   — 20 rows by default, segmented 20/50/100 toggle in the footer
//   · internal scroll — the body scrolls inside the card with sticky column
//     headers (callers must use borderCollapse: separate + sticky th styles)
//   · drag-to-resize — 14px grip strip between table and footer (clamped)
//   · search — whole-dataset search (cap 100 matches) with per-cell highlight
//     left to the caller via the provided `query`
//   · footer copy + paging control
// The table itself is rendered by the caller through a render-prop so Map can
// draw draggable colored headers and Validate can draw flagged/editable cells:
//   <DataSheet … >{({ rowIndices, query }) => <table…/>}</DataSheet>

const SEARCH_MATCH_CAP = 100;

function SearchBox({ q, setQ, placeholder = "Search the sheet…", width = 180 }) {
  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: 8, top: 8, fontSize: 10, color: T.inkSoft }} />
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder={placeholder}
        style={{ height: 26, width, padding: "0 22px 0 24px", border: `1px solid ${T.border}`, borderRadius: T.radiusBtn, font: `400 12px ${T.fontSans}`, color: T.ink, outline: "none", background: T.bg, boxSizing: "border-box" }}
      />
      {q && (
        <button onClick={() => setQ("")} style={{ position: "absolute", right: 4, top: 5, width: 16, height: 16, border: "none", background: "transparent", cursor: "pointer", color: T.inkMuted, padding: 0 }}>
          <i className="fa-solid fa-xmark" style={{ fontSize: 10 }} />
        </button>
      )}
    </div>
  );
}

export default function DataSheet({
  icon = "file-csv",
  title,
  meta,                 // node or string after the title
  hint,                 // right-aligned hint in the header bar
  totalRows,
  matchRow,             // (rowIndex, lowerCaseQuery) => bool — searches the whole dataset
  visibleOverride = null, // row indices that bypass paging (e.g. "problem rows only")
  defaultBodyHeight = 118,
  minBodyHeight = 96,
  maxBodyHeight = 820,
  searchInHeader = true, // Map: search lives in the header bar; Validate puts it in `toolbar`
  toolbar = null,        // ({ searchBox }) => node — optional second bar (filters, badges)
  elevated = false,
  footerNote = "click any cell to edit it in place",
  onPageSizeSelect = null, // lets the caller clear a visibleOverride filter
  style = {},
  children,              // ({ rowIndices, query }) => table node
}) {
  const [pageSize, setPageSize] = useState(20);
  const [bodyH, setBodyH] = useState(defaultBodyHeight);
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();

  const startResize = e => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = bodyH;
    const move = ev => setBodyH(Math.max(minBodyHeight, Math.min(maxBodyHeight, startH + (ev.clientY - startY))));
    const up = () => { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  let rowIndices;
  if (ql && matchRow) {
    rowIndices = [];
    for (let i = 0; i < totalRows && rowIndices.length < SEARCH_MATCH_CAP; i++) {
      if (matchRow(i, ql)) rowIndices.push(i);
    }
  } else if (visibleOverride) {
    rowIndices = visibleOverride;
  } else {
    rowIndices = Array.from({ length: Math.min(pageSize, totalRows) }, (_, i) => i);
  }

  const searchBox = <SearchBox q={q} setQ={setQ} />;

  return (
    <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable, overflow: "hidden", boxShadow: elevated ? T.shadowMd : "none", transition: "box-shadow 180ms ease-out", ...style }}>
      <style>{`
        .mig-resize:hover { background: ${T.blue50} !important; }
        .mig-resize:hover .mig-resize-icon { color: ${T.brand} !important; }
        .mig-resize:hover .mig-resize-hint { opacity: 1 !important; }
      `}</style>

      {/* Header bar */}
      <div style={{ padding: "9px 14px", borderBottom: `1px solid ${T.borderSubtle}`, display: "flex", alignItems: "center", gap: 10 }}>
        <i className={`fa-solid fa-${icon}`} style={{ color: T.brand, fontSize: 14 }} />
        <span style={{ font: `700 13px ${T.fontSans}`, color: T.ink }}>{title}</span>
        <span style={{ font: `400 12px ${T.fontSans}`, color: T.inkSoft }}>
          {ql ? `${rowIndices.length} of ${totalRows} rows match` : meta}
        </span>
        {searchInHeader && <span style={{ marginLeft: "auto" }}>{searchBox}</span>}
        {hint && (
          <span style={{ marginLeft: searchInHeader ? 0 : "auto", font: `500 12px ${T.fontSans}`, color: T.inkMuted, whiteSpace: "nowrap" }}>
            <i className="fa-regular fa-hand-pointer" style={{ marginRight: 6 }} />{hint}
          </span>
        )}
      </div>

      {/* Optional toolbar (filters / badges / problem-only) */}
      {toolbar && (
        <div style={{ padding: "7px 14px", borderBottom: `1px solid ${T.borderSubtle}`, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {toolbar({ searchBox: searchInHeader ? null : searchBox })}
        </div>
      )}

      {/* Scrollable body with sticky headers (rendered by the caller) */}
      <div style={{ maxHeight: bodyH, overflow: "auto" }}>
        {children({ rowIndices, query: ql })}
      </div>

      {/* Resize handle */}
      <div
        className="mig-resize"
        onMouseDown={startResize}
        title="Drag to show more or fewer rows"
        style={{ height: 14, borderTop: `1px solid ${T.borderSubtle}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "ns-resize", background: T.white, userSelect: "none", transition: "background 120ms ease-out" }}
      >
        <i className="fa-solid fa-chevron-up mig-resize-icon" style={{ fontSize: 8, color: T.borderStrong }} />
        <i className="fa-solid fa-grip-lines mig-resize-icon" style={{ fontSize: 10, color: T.inkSoft }} />
        <i className="fa-solid fa-chevron-down mig-resize-icon" style={{ fontSize: 8, color: T.borderStrong }} />
        <span className="mig-resize-hint" style={{ font: `500 10px/12px ${T.fontSans}`, color: T.brand, opacity: 0, transition: "opacity 120ms ease-out" }}>drag to resize</span>
      </div>

      {/* Footer: count + paging */}
      <div style={{ padding: "6px 14px", font: `400 11.5px/15px ${T.fontSans}`, color: T.inkSoft, borderTop: `1px solid ${T.borderSubtle}`, display: "flex", alignItems: "center", gap: 8 }}>
        <span>
          {ql
            ? `${rowIndices.length} of ${totalRows} rows match "${q.trim()}"`
            : `Showing ${rowIndices.length} of ${totalRows} rows${footerNote ? ` · ${footerNote}` : ""}`}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ font: `500 11.5px ${T.fontSans}`, color: T.inkSoft }}>Rows</span>
          <div style={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: T.radiusBtn, overflow: "hidden" }}>
            {[20, 50, 100].map(n => {
              const active = pageSize === n && !visibleOverride && !ql;
              return (
                <button
                  key={n}
                  onClick={() => { setPageSize(n); onPageSizeSelect?.(n); }}
                  style={{ border: "none", borderLeft: n !== 20 ? `1px solid ${T.border}` : "none", background: active ? T.blue50 : T.white, color: active ? T.brand : T.inkMuted, font: `600 11.5px/16px ${T.fontSans}`, padding: "3px 10px", cursor: "pointer" }}
                >
                  {n}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
